import { createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles'
import { darken, lighten } from 'polished'

const brandColors = {
	primary: {
		main: '#1BA4DE',
		light: lighten(0.02, '#1BA4DE'),
		// "rgb(0, 41, 64)"
		// "rgb(51, 98, 124)"
		// #005564
		// 'rgba(0, 0, 0, 0.54)'
	},
	secondary: {
		main: '#F2AD14',
	},
	tertiary: {
		dark: darken(0.1, '#106488'),
		main: '#106488',
		light: lighten(0.09, '#106488'),
	},
}

/**
 * color configuration: https://material-ui.com/customization/palette/
 * palette default: https://material-ui.com/customization/default-theme/
 * dark, light and contrastText are calulated based on main
 */
export const muiTheme = createMuiTheme({
	breakpoints: {
		values: {
			xs: 380,
			sm: 569,
			md: 768,
			lg: 992,
			xl: 1200,
			max: 1366,
		},
	},
	palette: {
		...brandColors,
		action: {
			focus: '#0d8ed1',
		},
		badges: {
			article: '#00348D',
			category: brandColors.tertiary.main,
			editorsPick: brandColors.tertiary.main,
			question: '#008D64',
		},
		error: {
			main: '#ff4136',
		},
		grey: {
			50: '#F2F5F7',
			100: '#FAFAFA',
			200: '#EEEEEE',
			300: '#D7D7D7',
			400: '#B0B0B0',
			500: '#7A7A7A',
			600: '#505050',
			700: '#2D2D2D',
			800: '#181818',
			900: '#000000',
		},
		old: {
			green: '#A1D884',
		},
		text: {
			primary: '#49485B',
		},
		contrastThreshold: 3,
		tonalOffset: 0.2,
	},
	typography: {
		fontFamily: `'Silka', 'sans-serif'`,
		button: {
			fontFamily: `'Silka SemiBold', 'sans-serif'`,
			fontSize: '.8rem',
			letterSpacing: '2.4px',
		},
		h1: {
			fontFamily: 'Inter ExtraBold, sans-serif',
			marginBottom: '1rem',
			fontSize: '3.25rem',
		},
		h2: {
			fontFamily: 'Silka SemiBold',
			fontSize: '2.625rem',
		},
		subtitle1: {
			fontFamily: 'Inter SemiBold',
		},
		subtitle2: {
			fontFamily: 'Inter SemiBold',
		},
	},
})

const { breakpoints, palette } = muiTheme
export const bndMuiTheme = createMuiTheme({
	...responsiveFontSizes(muiTheme),
	palette: {
		...palette,
		link: {
			author: {
				hover: palette.tertiary.main,
			},
			breadcrumb: {
				hover: palette.tertiary.main, // same as authorbio??
			},
			content: {
				main: '#087EE2', // replace??
			},
		},
	},
	overrides: {
		MuiButton: {
			root: {
				background: palette.primary.main,
				color: palette.common.white,
			},
			text: {
				padding: '15px 8px',
			},
		},
		button: {
			Button: {
				borderRadius: '3px',
				'&.primary': {
					background: palette.primary.main,
					borderColor: palette.primary.main,
					color: palette.common.white,
					'&:disabled': {
						'&:hover': {
							color: palette.common.white,
						},
					},
				},
				'&.secondary': {
					background: palette.common.white,
					borderColor: palette.primary.main,
					color: '#3F3F3F',
					'&:disabled': {
						'&:hover': {
							color: '#3F3F3F',
						},
					},
					'&:hover': {
						background: '#64D1FF',
						borderColor: palette.primary.main,
					},
				},
			},
		},
		featuredTopics: {
			FeaturedTopicContainer: {
				background: palette.grey[700],
			},
			InnerFeaturedTopicContainer: {
				'& a:nth-of-type(odd)': {
					background: palette.grey[800],
				},
				'& a:nth-of-type(even)': {
					background: palette.grey[700],
				},
			},
			FeaturedTopicTitle: {
				background: palette.primary.main,
			},
		},
		gradientContainer: {
			GradientWrapper: {
				'&.mbp': {
					background: `linear-gradient(180deg, ${palette.grey[100]} 0%, ${palette.grey[200]} 100%)`,
				},
			},
		},
		simpleHeader: {
			HeaderContext: {
				light: {
					background: `linear-gradient(180deg, ${palette.grey[100]} 0%, ${palette.grey[200]} 100%)`,
				},
			},
			LinkBar: {
				[breakpoints.up('sm')]: {
					background: palette.grey[200],
				},
			},
			LinksWrapper: {
				background: palette.grey[200],
			},
			Title: {
				'&.mbp': {
					color: palette.grey[700],
				},
			},
			SponsoredContent: {
				color: palette.grey[700],
			},
			StreamText: {
				color: palette.grey[700],
			},
			SubTitle: {
				'&.mbp': {
					color: palette.grey[400],
				},
			},
		},
		subHubHamburgerMenu: {
			BusinessTopicsTitle: {
				color: palette.primary.main,
			},
			HubTitle: {
				color: palette.tertiary.main,
				fontFamily: 'Silka Medium', // see below
				'&:hover': {
					color: palette.primary.main,
				},
				'& *': {
					color: palette.primary.main,
				},
			},
			StyledLink: {
				color: palette.tertiary.main,
				fontFamily: 'Silka Medium', // GET FONTS FROM DESIGN TO STRUCTURE OUT TYPOGRAPHY

				'&.svgOverride': {
					'& *': {
						stroke: palette.tertiary.main,
					},
				},
				'&:hover': {
					color: palette.primary.main,
					'&.svgOverride': {
						'& *': {
							stroke: palette.primary.main,
						},
					},
				},
			},
		},
		subHubHeader: {
			Layer: {
				background: palette.primary.main,
			},
			NavigationBar: {
				background: palette.primary.main,
			},
			SearchIconWrapper: {
				'&:hover': {
					'& path, line': {
						stroke: palette.primary.main,
					},
				},
			},
		},
		subHubMenu: {
			HubLink: {
				lineHeight: '1.3rem',
				fontSize: '1rem',
				'&:hover': {
					color: palette.tertiary.main,
				},
			},
		},
	},
})
